import React, { useContext, useState } from "react";
import algoliasearch from "algoliasearch";
import AppContext from "../../AppContext";
import ShoppingContext from "../../contexts/ShoppingContext";
import OutsideClickHandler from "react-outside-click-handler";

import "../../styles/algolia.scss";

import { ReactComponent as MagnifyingGlassSvg } from "../../assets/icons/magnifying-glass-grey.svg";
import { langComponent } from "../../constants";
import useScreenSize from "../../hooks/useScreenSize";
import useUsers from "../../hooks/useUsers";
import useCompanies from "../../hooks/useCompanies";
import useWorks from "../../hooks/useWorks";
import { useLocation } from "react-router-dom";

const appId = import.meta.env.ALGOLIA_SEARCH_APP_ID;
const apiKey = import.meta.env.ALGOLIA_SEARCH_API_KEY;
const searchIndex = import.meta.env.ALGOLIA_SEARCH_INDEX;
const assetsUri = import.meta.env.ASSETS_URI;

const hitLimit = 10;
const SEARCH_TYPE = {
  user: "user",
  work: "work",
  company: "company",
  workType: "workType",
  styleType: "styleType",
  spaceType: "spaceType",
};

const AlgoliaSearch = ({ handleNavigateTo, onClose }) => {
  const algoliaSearchClient = algoliasearch(appId, apiKey);
  const algoliaSearchIndex = algoliaSearchClient.initIndex(searchIndex);
  algoliaSearchIndex.setSettings({
    searchableAttributes: [
      "value",
      "type",
      "workType",
      "spaceType",
      "styleType",
      "keywords",
    ],
  });

  const { viewProject, viewProfile, getLang } = useContext(AppContext);
  const { setShoppingFilter } = useContext(ShoppingContext);
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const Translation = getLang(langComponent.header);
  const { screenSize } = useScreenSize();
  const { queryUsers } = useUsers();
  const { queryCompany } = useCompanies();
  const { queryWorks } = useWorks();
  const location = useLocation();

  const handleChange = async (element) => {
    const search = element.target.value;
    setSearchText(search);

    if (!search) {
      setData([]);
      return;
    }

    const { hits } = await algoliaSearchIndex.search(search, {
      hitsPerPage: hitLimit,
    });

    if (!hits || hits.length === 0) {
      setData([]);
      return;
    }

    // * Map hits with users, companies, works
    const userIds = [];
    const companyIds = [];
    const workIds = [];

    for (const hit of hits) {
      if (hit.type === SEARCH_TYPE.user) {
        userIds.push(hit.key);
        continue;
      }

      if (hit.type === SEARCH_TYPE.company) {
        companyIds.push(hit.key);
        continue;
      }

      if (hit.type === SEARCH_TYPE.work) {
        workIds.push(hit.key);
        continue;
      }
    }

    // * Query data
    const _promises = [];

    let userResultIndex = undefined;
    let companyResultIndex = undefined;
    let workResultIndex = undefined;

    if (userIds.length > 0) {
      _promises.push(
        queryUsers({ _id: { $in: userIds } }, null, -1, {
          avatar: 1,
          firstname: 1,
          surname: 1,
        })
      );
      userResultIndex = _promises.length - 1;
    }

    if (companyIds.length > 0) {
      _promises.push(
        queryCompany({ _id: { $in: companyIds } }, null, -1, {
          logo: 1,
          name: 1,
        })
      );
      companyResultIndex = _promises.length - 1;
    }

    if (workIds.length > 0) {
      _promises.push(
        queryWorks({ _id: { $in: workIds } }, null, -1, {
          banner: 1,
          name: 1,
          type: 1,
        })
      );
      workResultIndex = _promises.length - 1;
    }

    const results = await Promise.allSettled(_promises);

    const _data = [];

    for (const _hit of hits) {
      // * Map users
      if (_hit.type === SEARCH_TYPE.user) {
        const _user = results[userResultIndex]?.value?.data?.find(
          (u) => u._id === _hit.key
        );

        if (!_user) continue;

        _data.push({
          key: _user._id,
          type: _hit.type,
          value: `${_user.firstname} ${_user.surname ?? ""}`,
          icon: _user.avatar,
          meta: "Profile",
        });
        continue;
      }

      // * Map company
      if (_hit.type === SEARCH_TYPE.company) {
        const _company = results[companyResultIndex]?.value?.data?.find(
          (u) => u._id === _hit.key
        );

        if (!_company) continue;

        _data.push({
          key: _company._id,
          type: _hit.type,
          value: _company.name,
          icon: _company.logo,
          meta: "Company",
        });

        continue;
      }

      // * Map work
      if (_hit.type === SEARCH_TYPE.work) {
        const _work = results[workResultIndex]?.value?.data?.find(
          (u) => u._id === _hit.key
        );

        if (!_work) continue;

        let meta = "";

        if (_work.type === 1) meta = "Panorama";
        if (_work.type === 2) meta = "Work Exchange";
        if (_work.type === 3) meta = "Images";

        _data.push({
          key: _work._id,
          type: _hit.type,
          value: _work.name,
          icon: _work.banner,
          workType: _work.type,
          meta,
        });

        continue;
      }
    }

    setData(_data);
  };

  const handleClick = async (e) => {
    onClose && onClose();

    const { key, type, workType } = e;
    setSearchText("");
    setData([]);

    if (type === SEARCH_TYPE.user) {
      viewProfile(key);
      return;
    }

    if (type === SEARCH_TYPE.work && workType === 2) {
      handleNavigateTo(`/work-exchanges/${key}`);
      return;
    }

    if (type === SEARCH_TYPE.work) {
      viewProject(key, location);
      return;
    }

    if (type === SEARCH_TYPE.company) {
      handleNavigateTo(`/companies/${key}`);
      return;
    }

    if (type === SEARCH_TYPE.workType) {
      setShoppingFilter({ typeOfWork: key, sort: { createdOn: -1 } });
      handleNavigateTo(`/shopping`);
      return;
    }

    if (type === SEARCH_TYPE.spaceType) {
      setShoppingFilter({ typeOfSpace: key, sort: { createdOn: -1 } });
      handleNavigateTo(`/shopping`);
      return;
    }

    if (type === SEARCH_TYPE.styleType) {
      setShoppingFilter({ typeOfStyle: key, sort: { createdOn: -1 } });
      handleNavigateTo(`/shopping`);
      return;
    }

    return;
  };

  const classNames = ["algolia"];

  if (["xs", "s", "sm", "md"].includes(screenSize))
    classNames.push("algolia--mobile");

  return (
    <OutsideClickHandler onOutsideClick={() => setData([])} display="flex">
      <div className={classNames.join(" ")}>
        <div className="algolia-search">
          <MagnifyingGlassSvg />
          <input
            className="algolia-search__input"
            type="text"
            value={searchText}
            onChange={handleChange}
            placeholder={Translation("Search")}
          />
        </div>
        {data && data.length > 0 && (
          <div className="algolia-dropdown">
            {data.map((m, index) => {
              const { value, icon, meta } = m;
              return (
                <div
                  key={`algolia-dropdown-item-${index}`}
                  className="algolia-dropdown-item"
                  onClick={() => handleClick(m)}
                >
                  <div className="algolia-dropdown-item-icon">
                    <img
                      className="algolia-dropdown-item-icon__image"
                      alt="icon"
                      src={`${assetsUri}/${icon}`}
                      onError={({ target }) => {
                        target.src = icon;
                      }}
                    />
                  </div>
                  <div className="algolia-dropdown-item__name">{value}</div>
                  <div className="algolia-dropdown-item__meta">{meta}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </OutsideClickHandler>
  );
};

AlgoliaSearch.propTypes = {};

export default AlgoliaSearch;
